import React from 'react';
import { Box } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { apiChangePassword } from 'API/lotteryApi';
import { useAppDispatch } from 'store/store';
import { addAlert } from 'store/reducers/alertReducer';
import { CustomTextField } from 'components/FormFields/CustomTextField/CustomTextField';
import RedButton from 'Styled/RedButton';
import TextButton from 'Styled/TextButton';
import { PageHeader } from 'components/Layout/PageHeader';
import { PASSWORD_HINT, passwordSchema } from 'utility/passwordRules';

const validationSchema = Yup.object({
  oldPassword: Yup.string().required('Aktuelles Passwort ist erforderlich'),
  newPassword: passwordSchema.required('Neues Passwort ist erforderlich'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwörter stimmen nicht überein')
    .required('Passwortbestätigung ist erforderlich'),
});

export const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  return (
    <Box>
      <PageHeader title="Passwort ändern" />

      <Formik
        initialValues={{ oldPassword: '', newPassword: '', confirmPassword: '' }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm, setFieldError }) => {
          try {
            await apiChangePassword(values.oldPassword, values.newPassword);
            dispatch(addAlert({ message: 'Passwort erfolgreich geändert.', severity: 'success' }));
            resetForm();
            navigate('/');
          } catch (err: unknown) {
            const message = (err as { message?: string })?.message;
            setFieldError('oldPassword', message ?? 'Passwort konnte nicht geändert werden.');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form>
            <Box sx={{ maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <CustomTextField
                name="oldPassword"
                autoComplete="current-password"
                label="Aktuelles Passwort"
                type="password"
                value={values.oldPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.oldPassword && Boolean(errors.oldPassword)}
                helperText={touched.oldPassword ? errors.oldPassword : ''}
              />
              <CustomTextField
                name="newPassword"
                autoComplete="new-password"
                label="Neues Passwort"
                type="password"
                value={values.newPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.newPassword && Boolean(errors.newPassword)}
                helperText={touched.newPassword ? errors.newPassword : PASSWORD_HINT}
              />
              <CustomTextField
                name="confirmPassword"
                autoComplete="new-password"
                label="Passwort bestätigen"
                type="password"
                value={values.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                helperText={touched.confirmPassword ? errors.confirmPassword : ''}
              />
              <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
                <RedButton type="submit" disabled={isSubmitting}>
                  Speichern
                </RedButton>
                <TextButton onClick={() => navigate(-1)}>Abbrechen</TextButton>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
